import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HEX_COLOR_REGEX,
  PASSWORD_MIN_LENGTH,
  ROLES,
  UserCreateSchema,
  UserUpdateSchema,
  type PasswordResetRequest,
  type Role,
  type ServiceItem,
  type UserCreateRequest,
  type UserDetail,
  type UserUpdateRequest,
} from '@crm/shared';
import { api, extractApiError } from '../../lib/api';
import { useServicesList } from '../services/api';
import {
  useUserCreate,
  useUserPasswordReset,
  useUserServicesSet,
  useUserUpdate,
} from './api';

type Props = {
  open: boolean;
  user: UserDetail | null;
  currentUserId: number;
  onClose: () => void;
};

type Form = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: Role;
  bookable: boolean;
  color: string;
  bio: string;
  active: boolean;
};

const EMPTY: Form = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'EMPLOYEE',
  bookable: true, // domyślnie EMPLOYEE = bookable; admin/recepcja zmienią
  color: '#ec4899',
  bio: '',
  active: true,
};

function fromUser(u: UserDetail): Form {
  return {
    email: u.email,
    password: '',
    firstName: u.firstName,
    lastName: u.lastName,
    role: u.role,
    bookable: u.bookable,
    color: u.color ?? '',
    bio: u.bio ?? '',
    active: u.active,
  };
}

export function EmployeeFormModal({
  open,
  user,
  currentUserId,
  onClose,
}: Props): ReactNode {
  const { t } = useTranslation();
  const isEdit = user !== null;

  const create = useUserCreate();
  const update = useUserUpdate(user?.id ?? 0);
  const setServices = useUserServicesSet(user?.id ?? 0);
  const passwordReset = useUserPasswordReset(user?.id ?? 0);

  // Lista usług potrzebna do multi-selecta — tylko aktywne (nie można przypisać nieaktywnej).
  const { data: services = [] } = useServicesList({ active: 'true' });

  const [form, setForm] = useState<Form>(EMPTY);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    setResetPwd('');
    setResetMsg(null);
    if (user) {
      setForm(fromUser(user));
      setServiceIds(user.serviceIds);
    } else {
      setForm(EMPTY);
      setServiceIds([]);
    }
  }, [open, user]);

  const grouped = useMemo(() => {
    const groups = new Map<string, ServiceItem[]>();
    for (const s of services) {
      const key = s.category ?? '—';
      const arr = groups.get(key) ?? [];
      arr.push(s);
      groups.set(key, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [services]);

  if (!open) return null;

  const isSelf = isEdit && user?.id === currentUserId;
  const showServices = form.bookable;

  const toggleService = (id: number) =>
    setServiceIds((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id],
    );

  const toggleBookable = (next: boolean) => {
    // Odznaczanie bookable przy istniejących specjalizacjach = utrata mapowań.
    // Backend by zwrócił 400; lepiej zapytać w UI i wyczyścić od razu.
    if (!next && serviceIds.length > 0) {
      const ok = window.confirm(
        t('employees.form.bookableClearConfirm', { n: serviceIds.length }),
      );
      if (!ok) return;
      setServiceIds([]);
    }
    setForm((f) => ({ ...f, bookable: next }));
  };

  const validate = () => {
    const candidate: Record<string, unknown> = {
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      role: form.role,
      bookable: form.bookable,
      color: form.color.trim() === '' ? null : form.color,
      bio: form.bio.trim() === '' ? null : form.bio,
    };
    if (!isEdit) candidate.password = form.password;
    if (isEdit) candidate.active = form.active;

    const schema = isEdit ? UserUpdateSchema : UserCreateSchema;
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const fld: Partial<Record<keyof Form, string>> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (
          path === 'email' ||
          path === 'password' ||
          path === 'firstName' ||
          path === 'lastName' ||
          path === 'role' ||
          path === 'color' ||
          path === 'bio'
        ) {
          fld[path] = issue.message;
        }
      }
      // Dodatkowy lokalny check koloru (UpdateSchema akceptuje optional null).
      if (form.color.trim() !== '' && !HEX_COLOR_REGEX.test(form.color)) {
        fld.color = t('employees.form.errors.colorFormat');
      }
      setErrors(fld);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const parsed = validate();
    if (!parsed) return;

    try {
      let savedId: number;
      if (isEdit && user) {
        await update.mutateAsync(parsed as UserUpdateRequest);
        savedId = user.id;
      } else {
        const created = await create.mutateAsync(parsed as UserCreateRequest);
        savedId = created.id;
      }

      // Przypisanie usług — tylko gdy pracownik jest "bookable" i lista mogła
      // się zmienić. Backend i tak odrzuci próbę przypisania usług do
      // non-bookable, ale pomijamy zbędny request.
      if (form.bookable) {
        const before = user?.serviceIds ?? [];
        const changed =
          before.length !== serviceIds.length ||
          !before.every((id) => serviceIds.includes(id));
        if (changed || !isEdit) {
          if (!isEdit) {
            // Hook setServices jest zbindowany do user?.id z renderu (0 przy create).
            // W trybie create wywołujemy endpoint bezpośrednio na savedId.
            await api.put(`/users/${savedId}/services`, { serviceIds });
          } else {
            await setServices.mutateAsync({ serviceIds });
          }
        }
      }
      // Uwaga: scenariusz „odznacz bookable u pracownika z usługami" jest
      // obsłużony w handlerze checkbox'a (auto-clear z confirmem) — submit
      // zawsze dostaje spójny stan.

      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err));
    }
  };

  const onResetPassword = async () => {
    setResetMsg(null);
    if (resetPwd.length < PASSWORD_MIN_LENGTH) {
      setResetMsg(t('employees.form.errors.passwordTooShort', { n: PASSWORD_MIN_LENGTH }));
      return;
    }
    try {
      await passwordReset.mutateAsync({ password: resetPwd } satisfies PasswordResetRequest);
      setResetPwd('');
      setResetMsg(t('employees.form.resetOk'));
    } catch (err) {
      setResetMsg(extractApiError(err));
    }
  };

  const submitting = create.isPending || update.isPending || setServices.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {isEdit ? t('employees.form.titleEdit') : t('employees.form.titleCreate')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('employees.form.firstName')} error={errors.firstName}>
              <input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                className="input"
                required
                autoFocus
              />
            </Field>
            <Field label={t('employees.form.lastName')} error={errors.lastName}>
              <input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                className="input"
                required
              />
            </Field>
          </div>

          <Field label={t('employees.form.email')} error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="input"
              autoComplete="email"
              required
            />
          </Field>

          {!isEdit && (
            <Field
              label={t('employees.form.password')}
              error={errors.password}
              hint={t('employees.form.passwordHint', { n: PASSWORD_MIN_LENGTH })}
            >
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="input font-mono"
                autoComplete="new-password"
                required
              />
            </Field>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('employees.form.role')} error={errors.role}>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
                className="input"
                disabled={isSelf}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`roles.${r}`)}
                  </option>
                ))}
              </select>
              {isSelf && (
                <p className="mt-1 text-xs text-zinc-500">
                  {t('employees.form.selfRoleLocked')}
                </p>
              )}
            </Field>
            <Field label={t('employees.form.color')} error={errors.color}>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={HEX_COLOR_REGEX.test(form.color) ? form.color : '#ec4899'}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="h-9 w-12 cursor-pointer rounded-md border border-zinc-300 bg-white"
                />
                <input
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="input font-mono"
                  placeholder="#ec4899"
                />
              </div>
            </Field>
            {isEdit && (
              <Field label={t('employees.form.status')}>
                <label className="mt-2 inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    disabled={isSelf}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                  />
                  {form.active
                    ? t('common.status.active')
                    : t('common.status.inactive')}
                </label>
              </Field>
            )}
          </div>

          <Field label={t('employees.form.bio')} error={errors.bio}>
            <textarea
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              rows={2}
              className="input resize-none"
              placeholder={t('employees.form.bioPlaceholder')}
            />
          </Field>

          <div className="rounded-lg border border-zinc-200 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.bookable}
                onChange={(e) => toggleBookable(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium text-zinc-800">
                  {t('employees.form.bookable')}
                </span>
                <span className="block text-xs text-zinc-500">
                  {t('employees.form.bookableHint')}
                </span>
              </span>
            </label>
          </div>

          {showServices && (
            <div className="rounded-lg border border-zinc-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-zinc-700">
                  {t('employees.form.services')}
                </div>
                <div className="text-xs text-zinc-500">
                  {t('employees.form.servicesSelected', { n: serviceIds.length })}
                </div>
              </div>
              {grouped.length === 0 ? (
                <div className="text-sm text-zinc-500">{t('employees.form.noServices')}</div>
              ) : (
                <div className="space-y-3">
                  {grouped.map(([cat, items]) => (
                    <div key={cat}>
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        {cat}
                      </div>
                      <div className="grid gap-1 sm:grid-cols-2">
                        {items.map((s) => (
                          <label
                            key={s.id}
                            className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-zinc-50"
                          >
                            <input
                              type="checkbox"
                              checked={serviceIds.includes(s.id)}
                              onChange={() => toggleService(s.id)}
                              className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span className="flex-1 truncate">{s.name}</span>
                            <span className="text-xs text-zinc-400">
                              {s.durationMinutes} {t('common.minutesShort')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {isEdit && (
            <div className="rounded-lg border border-zinc-200 p-3">
              <div className="mb-2 text-sm font-medium text-zinc-700">
                {t('employees.form.passwordReset')}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  placeholder={t('employees.form.passwordResetPlaceholder')}
                  className="input flex-1 min-w-[12rem] font-mono"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => void onResetPassword()}
                  disabled={passwordReset.isPending || !resetPwd}
                  className="btn-secondary"
                >
                  {passwordReset.isPending ? t('common.saving') : t('employees.form.resetButton')}
                </button>
              </div>
              {resetMsg && (
                <p className="mt-2 text-sm text-zinc-600">{resetMsg}</p>
              )}
            </div>
          )}

          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-700">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && !error && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
